/* ============================================================
   As datas de um desafio

   Antes dava para criar um desafio que começava no mês passado, ou que
   já tinha terminado. Ninguém posta no passado de propósito: era sempre
   o calendário aberto no dia errado, e o desafio nascia encerrado ou
   com um "desde" que não aconteceu.

   As duas telas (site e app) perguntam aqui, para dizerem a mesma coisa.
   Os dias são "AAAA-MM-DD", comparáveis como texto.
   ============================================================ */

export interface DatasDoDesafio {
  startDay: string;
  endDay: string;
}

/**
 * O que está errado nas datas, ou `null`.
 *
 * `antes` é o desafio como está salvo, na edição. Um começo que já
 * passou e não foi mexido continua valendo — o desafio que começou em
 * agosto não fica preso por isso —, mas MUDAR para uma data passada não.
 * O fim pode ser hoje (é o jeito de encerrar), nunca antes de hoje.
 */
export function erroNasDatas(
  datas: DatasDoDesafio, hoje: string, antes?: DatasDoDesafio,
): string | null {
  const { startDay, endDay } = datas;
  if (!startDay) return "Escolhe quando o desafio começa.";
  const comecoMudou = !antes || antes.startDay !== startDay;
  if (comecoMudou && startDay < hoje) return "O começo não pode ser numa data que já passou.";
  const fimMudou = !antes || antes.endDay !== endDay;
  if (endDay && fimMudou && endDay < hoje) return "O fim não pode ser numa data que já passou.";
  if (endDay && !(startDay < endDay)) return "O fim tem que ser depois do começo.";
  return null;
}

/**
 * Em quais outros desafios um prato do dia `dia` pode aparecer.
 *
 * Desafio sem data de fim tem `endDay` vazio — e é o caso normal. O app
 * comparava `endDay >= hoje` direto, e "" nunca é maior que uma data: os
 * desafios sem fim sumiam da lista de "postar também em". A comparação
 * mora aqui para as duas telas não divergirem de novo.
 */
export function desafiosQueAceitamODia<T extends { id: string; name: string; startDay: string; endDay?: string | null }>(
  meus: readonly T[], cidAtual: string, dia: string,
): T[] {
  return meus
    .filter((d) => d.id !== cidAtual && (d.startDay ?? "") <= dia && dia <= (d.endDay || "9999-12-31"))
    .sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
}

/* ---------- a janela do ranking e do recap ---------- */

export type PeriodoDoRanking = "week" | "month" | "year" | "all";

const somar = (dia: string, n: number): string => {
  const [a, m, d] = dia.split("-").map(Number);
  return new Date(Date.UTC(a ?? 1970, (m ?? 1) - 1, (d ?? 1) + n)).toISOString().slice(0, 10);
};
const diaDaSemana = (dia: string): number => {
  const [a, m, d] = dia.split("-").map(Number);
  return new Date(Date.UTC(a ?? 1970, (m ?? 1) - 1, d ?? 1)).getUTCDay();
};

/**
 * De quando a quando o ranking (e o recap) olham.
 *
 * Havia quatro cópias desta conta, e todas erravam igual:
 *
 *  - "Todas" ia até `fimEfetivo`, que é 2999-12-31 para desafio sem fim
 *    — a tela escrevia "4 de setembro de 2026 a 31 de dezembro de 2999";
 *  - "Ano" e "Mês" começavam antes do próprio desafio ("1º de janeiro"
 *    num desafio de setembro);
 *  - desafio encerrado mostrava a semana de HOJE, vazia, em vez da
 *    última semana em que ele rodou.
 *
 * Agora a referência é hoje, ou o último dia do desafio se ele já
 * acabou, e a janela nunca sai de [começo, fim]. A semana vai de segunda
 * a domingo — a mesma das temporadas.
 */
export function janelaDoPeriodo(
  periodo: PeriodoDoRanking, hoje: string,
  desafio: { startDay?: string | null; endDay?: string | null } | null | undefined,
): { de: string; ate: string } {
  const comeco = desafio?.startDay || "2000-01-01";
  const fim = desafio?.endDay || "";
  const ref = fim && fim < hoje ? fim : hoje;
  let de: string;
  if (periodo === "all") de = comeco;
  else if (periodo === "week") de = somar(ref, -((diaDaSemana(ref) + 6) % 7));
  else if (periodo === "month") de = `${ref.slice(0, 7)}-01`;
  else de = `${ref.slice(0, 4)}-01-01`;
  if (de < comeco) de = comeco;
  const ate = ref < de ? de : ref;
  return { de, ate };
}

/** A janela do mesmo tamanho logo antes — a base do "comparado com". */
export function janelaAnterior(
  periodo: PeriodoDoRanking, atual: { de: string; ate: string },
): { de: string; ate: string } | null {
  if (periodo === "all") return null;
  const [a1, m1, d1] = atual.de.split("-").map(Number);
  const [a2, m2, d2] = atual.ate.split("-").map(Number);
  const dias = Math.max(1, Math.round(
    (Date.UTC(a2 ?? 0, (m2 ?? 1) - 1, d2 ?? 1) - Date.UTC(a1 ?? 0, (m1 ?? 1) - 1, d1 ?? 1)) / 86400000) + 1);
  const ate = somar(atual.de, -1);
  return { de: somar(ate, -(dias - 1)), ate };
}
